import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import { ZODIAC_SIGNS } from '../../data/fortuneData';
import ZodiacCard from '../../components/fortune/ZodiacCard';
import FortuneContent from '../../components/fortune/FortuneContent';
import AdBanner from '../../components/AdBanner';
import { getZodiacFortune } from '../../services/api';
import './FortuneCategoryPage.css';

function ZodiacPage() {
  const [fortune, setFortune] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fortuneCache, setFortuneCache] = useState(new Map());

  // Prefetch all fortunes on page load
  useEffect(() => {
    const fetchAllFortunes = async () => {
      const cache = new Map();

      try {
        // Fetch all 12 fortunes in parallel
        const promises = ZODIAC_SIGNS.map(sign =>
          getZodiacFortune(sign.id)
            .then(response => {
              if (response.success) {
                cache.set(sign.id, {
                  title: `${sign.emoji} ${sign.name} (${sign.period})`,
                  date: response.data.date,
                  overall: response.data.overall,
                  love: response.data.love,
                  money: response.data.money,
                  health: response.data.health,
                  advice: response.data.advice,
                  avoid: response.data.avoid,
                  luckyNumbers: response.data.luckyNumbers,
                  luckyColor: response.data.luckyColor,
                  luckyDirection: response.data.luckyDirection
                });
              }
            })
            .catch(err => console.error(`Error fetching ${sign.id}:`, err))
        );

        await Promise.all(promises);
        setFortuneCache(cache);
      } catch (error) {
        console.error('Error prefetching fortunes:', error);
      }
    };

    fetchAllFortunes();
  }, []);

  const handleSelectSign = (sign) => {
    // Get from cache instantly
    const cachedFortune = fortuneCache.get(sign.id);

    if (cachedFortune) {
      setFortune(cachedFortune);

      // Scroll to fortune content
      setTimeout(() => {
        const element = document.querySelector('.fortune-content');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Fallback to API if cache miss (shouldn't happen)
      fetchFortuneFromAPI(sign);
    }
  };

  const fetchFortuneFromAPI = async (sign) => {
    setLoading(true);

    try {
      const response = await getZodiacFortune(sign.id);
      if (response.success) {
        const fortuneData = {
          title: `${sign.emoji} ${sign.name} (${sign.period})`,
          date: response.data.date,
          overall: response.data.overall,
          love: response.data.love,
          money: response.data.money,
          health: response.data.health,
          advice: response.data.advice,
          avoid: response.data.avoid,
          luckyNumbers: response.data.luckyNumbers,
          luckyColor: response.data.luckyColor,
          luckyDirection: response.data.luckyDirection
        };

        setFortune(fortuneData);

        // Scroll to fortune content
        setTimeout(() => {
          const element = document.querySelector('.fortune-content');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching zodiac fortune:', error);
      alert('운세를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fortune-category-page">
      <SEO
        title="AI 별자리 운세 - 인공지능 12별자리 오늘의 운세"
        description="AI 별자리 운세! 인공지능이 분석하는 12별자리별 오늘의 운세. 양자리, 황소자리 등 총운, 애정운, 금전운, 건강운과 AI 행운의 숫자를 확인하세요."
        keywords="AI 별자리 운세, 인공지능 별자리 운세, AI 운세, 12별자리운세, 별자리운세, 양자리운세, 황소자리운세, 쌍둥이자리운세, 오늘의별자리, GPT 운세, AI 행운의 숫자"
        url="https://lottounse.kr/fortune/zodiac"
      />
      <div className="category-header zodiac-header">
        <h1>♈ 12별자리 운세</h1>
        <p>당신의 별자리를 선택하세요</p>
      </div>

      <AdBanner />

      <div className="category-content">
        <div className="cards-grid">
          {ZODIAC_SIGNS.map((sign) => (
            <ZodiacCard
              key={sign.id}
              sign={sign}
              onClick={handleSelectSign}
            />
          ))}
        </div>

        {loading && (
          <div className="loading-section">
            <p>운세를 불러오는 중...</p>
          </div>
        )}

        {fortune && !loading && (
          <>
            <AdBanner />
            <FortuneContent fortune={fortune} type="zodiac" />
          </>
        )}
      </div>

      <AdBanner />
    </div>
  );
}

export default ZodiacPage;
